import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { ArrowLeft, Tag } from 'lucide-react';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

// ── Stripe setup ────────────────────────────────────────────────────────────

const STRIPE_PK_TEST = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_TEST as string | undefined;
const STRIPE_PK_LIVE = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_LIVE as string | undefined;

const stripePromiseCache: Record<string, ReturnType<typeof loadStripe>> = {};
function getStripePromise(mode: 'test' | 'live') {
  const key = mode === 'test' ? STRIPE_PK_TEST : STRIPE_PK_LIVE;
  if (!key) return null;
  if (!stripePromiseCache[key]) {
    stripePromiseCache[key] = loadStripe(key);
  }
  return stripePromiseCache[key];
}

// ── Types ───────────────────────────────────────────────────────────────────

interface CheckoutLocationState {
  priceId: string;
  seats: number;
  couponCode?: string;
  /** Where to go if the user clicks "Back" */
  returnTo?: string;
}

// ── Page ────────────────────────────────────────────────────────────────────

export function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as CheckoutLocationState | null;

  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [session, setSession] = useState<{ clientSecret: string; stripeMode: 'test' | 'live' } | null>(null);

  const priceId = state?.priceId;
  const seats = state?.seats;
  const couponCode = state?.couponCode;

  // Create the Checkout Session up front. The session tells us whether the
  // org bills in test or live mode, and Stripe.js must be loaded with the
  // publishable key for that same mode. Previously Stripe.js was initialised
  // with the test key while the session was still loading, which silently
  // broke checkout for live-mode (production) orgs.
  useEffect(() => {
    if (!priceId) return;
    let cancelled = false;
    setSession(null);
    setError(null);

    api.post<{
      success: boolean;
      data: { clientSecret: string; stripeMode: 'test' | 'live' };
    }>('/billing/create-checkout-session', {
      priceId,
      seats,
      ...(couponCode ? { couponCode } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        if (!res.data.clientSecret) {
          setError('Failed to initialize checkout.');
          return;
        }
        setSession({ clientSecret: res.data.clientSecret, stripeMode: res.data.stripeMode });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to start checkout.');
      });

    return () => { cancelled = true; };
  }, [priceId, seats, couponCode, retryKey]);

  // No state passed — the user navigated here directly
  if (!state?.priceId) {
    return (
      <div className="min-h-screen bg-mist flex flex-col items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-brand-md border border-stone/60 px-6 py-8 max-w-sm w-full text-center">
          <p className="text-sm text-slate mb-4">
            No checkout session found. Please select a plan first.
          </p>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="btn-primary"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-mist flex flex-col items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-brand-md border border-stone/60 px-6 py-8 max-w-sm w-full text-center">
          <p className="text-sm text-red-600 mb-4">{error}</p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => { setError(null); setRetryKey((k) => k + 1); }}
              className="btn-primary w-full"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={() => navigate(state.returnTo ?? '/', { replace: true })}
              className="btn-secondary w-full"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Wait for the session before touching Stripe.js so the publishable key
  // matches the session's mode.
  if (!session) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const stripePromise = getStripePromise(session.stripeMode);

  if (!stripePromise) {
    return (
      <div className="min-h-screen bg-mist flex flex-col items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-brand-md border border-stone/60 px-6 py-8 max-w-sm w-full text-center">
          <p className="text-sm text-red-600 mb-4">
            Stripe is not configured. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-stone/60 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(state.returnTo ?? '/', { replace: true })}
            className="flex items-center gap-1 text-sm text-slate hover:text-midnight
                       transition-colors"
          >
            <ArrowLeft size={16} />
            Back
          </button>
          <h1 className="font-display text-lg font-semibold text-midnight">
            Checkout
          </h1>
        </div>
      </div>

      {/* Coupon badge */}
      {state.couponCode && (
        <div className="px-4 pt-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-green-700">
              <Tag size={14} className="shrink-0" />
              Coupon <span className="font-semibold">{state.couponCode}</span> will be applied to this order.
            </div>
          </div>
        </div>
      )}

      {/* Embedded checkout */}
      <div className="flex-1 px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {/* Stripe.js rejects an `appearance` option on initEmbeddedCheckout
              ("appearance is not an accepted parameter") and react-stripe-js
              swallows the rejection, leaving this area blank. Embedded
              Checkout is branded from the Stripe Dashboard (Settings →
              Branding) instead, so pass only the client secret. */}
          <EmbeddedCheckoutProvider
            key={`${retryKey}-${session.stripeMode}`}
            stripe={stripePromise}
            options={{ clientSecret: session.clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
}
